import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FilterCheckIcon } from "@/components/icons/FilterCheckIcon";
import { FilterIcon } from "@/components/icons/FilterIcon";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDashboard } from "@/contexts/DashboardContext";
import {
  fetchSegmentStreamByCallUniqueId,
  type SegmentStreamRecord,
} from "@/lib/axios/api/call-quality.api";
import { cn } from "@/lib/utils";
import { convertLocalTimezone } from "@/utils/dateUtils";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Info, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import React, { useEffect, useMemo, useState } from "react";
import type { CallQualityDetailRow, CallQualityStatus } from "./CallQualityDetailWidget";
import { CQD_COLORS_LIGHT, CQD_COLORS_DARK } from "./CallQualityDistributionWidget";

/** Màu badge Quality = CQD_COLORS_LIGHT / CQD_COLORS_DARK (cùng chart Call Quality Distribution) */
function getQualityBadgeStyle(
  status: CallQualityStatus,
  theme: "light" | "dark"
): { backgroundColor: string; color: string } {
  const colors = theme === "light" ? CQD_COLORS_LIGHT : CQD_COLORS_DARK;
  const darkText = "#0C3451";
  const whiteText = "#fff";
  switch (status) {
    case "Good":
      return { backgroundColor: colors[0], color: whiteText };
    case "Poor":
    case "Bad":
      return { backgroundColor: colors[1], color: darkText };
    case "Unclassified":
    default:
      return { backgroundColor: colors[2], color: whiteText };
  }
}

interface CallQualityDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: CallQualityDetailRow | null;
  /** Passed from CQDTab; reserved for contextual detail (e.g. failure type) */
  failureTypeContext?: unknown;
}

type CallQualityDetailsSortKey =
  | "startTime"
  | "stream"
  | "quality"
  | "duration"
  | "avgJitter"
  | "avgPackageLost"
  | "avgRoundTrip"
  | "packetUtilization"
  | "streamId";

interface CallQualityDetailsTableRow {
  id: string;
  startTimeText: string;
  startTimeValue: number | null;
  streamId: string;
  streamDirection: React.ReactNode;
  streamText: string;
  quality: string;
  qualityStyle: { backgroundColor: string; color: string };
  duration: string;
  durationText: string;
  durationValue: number | null;
  avgJitter: React.ReactNode;
  avgJitterText: string;
  avgJitterValue: number | null;
  avgPackageLost: React.ReactNode;
  avgPackageLostText: string;
  avgPackageLostValue: number | null;
  avgRoundTrip: React.ReactNode;
  avgRoundTripText: string;
  avgRoundTripValue: number | null;
  packetUtilization: React.ReactNode;
  packetUtilizationText: string;
  packetUtilizationValue: number | null;
}

const DISPLAY_NOT_AVAILABLE = "-";
const INTERNAL_NOT_AVAILABLE = "N/A";

const STREAM_MS_FIELDS = new Set([
  "averageJitter",
  "maxJitter",
  "averageRoundTripTime",
  "maxRoundTripTime",
  "averageAudioNetworkJitter",
  "maxAudioNetworkJitter",
  "rmsFreezeDuration",
  "freezeDuration",
]);
const STREAM_PERCENT_FIELDS = new Set([
  "averagePacketLossRate",
  "maxPacketLossRate",
  "averageRatioOfConcealedSamples",
  "maxRatioOfConcealedSamples",
  "wasMediaBypassed",
  "postForwardErrorCorrectionPacketLossRate",
  "averageVideoFrameLossPercentage",
  "lowFrameRateRatio",
  "averageVideoPacketLossRate",
  "lowVideoProcessingCapabilityRatio",
  "freezeDurationRatio",
]);

function formatNumber(value: number, maximumFractionDigits = 2, minimumFractionDigits = 0): string {
  return value.toLocaleString(undefined, { minimumFractionDigits, maximumFractionDigits });
}

function getQualityClassName(quality?: string | null): string {
  const normalized = (quality ?? "").toLowerCase();
  if (normalized === "good") return "text-green-700 dark:text-green-300";
  if (normalized === "poor" || normalized === "bad") return "text-red-700 dark:text-red-300";
  return "text-slate-600 dark:text-slate-300";
}

function normalizeQualityLabel(quality?: string | null): string {
  const raw = String(quality ?? "").trim();
  if (!raw) return "Unclassified";
  if (raw.toLowerCase() === "bad") return "Poor";
  return raw;
}

function getDirectionLabel(direction?: string, callerName: string = "Caller", calleeName: string = "Callee"): string {
  if (direction === "callerToCallee") return `${callerName} -> ${calleeName}`;
  if (direction === "calleeToCaller") return `${calleeName} -> ${callerName}`;
  return direction ?? "Unknown";
}

function getFailureReasonLabel(reason?: string | null): string | null {
  const normalized = String(reason ?? "").trim();
  if (!normalized) return null;
  if (normalized.toLowerCase() === "none") return null;
  return normalized.length > 0 ? normalized : null;
}

function formatSecondsToHMS(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function formatDurationValue(value?: number | null): string {
  if (typeof value !== "number") return INTERNAL_NOT_AVAILABLE;
  return formatSecondsToHMS(value);
}

function formatPacketLossValue(value?: number | null): string {
  if (typeof value !== "number") return INTERNAL_NOT_AVAILABLE;
  const packetLossPercent = value <= 1 ? value * 100 : value;
  return `${formatNumber(packetLossPercent)}%`;
}

function formatMetricText(value: number | null | undefined, unit: string): string {
  if (typeof value !== "number") return INTERNAL_NOT_AVAILABLE;
  return `${formatNumber(value)} ${unit}`;
}

function getSortValue(row: CallQualityDetailsTableRow, key: CallQualityDetailsSortKey): string | number {
  switch (key) {
    case "startTime":
      return row.startTimeValue ?? Number.NEGATIVE_INFINITY;
    case "stream":
      return row.streamText.toLowerCase();
    case "quality":
      return row.quality.toLowerCase();
    case "duration":
      return row.durationValue ?? Number.NEGATIVE_INFINITY;
    case "avgJitter":
      return row.avgJitterValue ?? Number.NEGATIVE_INFINITY;
    case "avgPackageLost":
      return row.avgPackageLostValue ?? Number.NEGATIVE_INFINITY;
    case "avgRoundTrip":
      return row.avgRoundTripValue ?? Number.NEGATIVE_INFINITY;
    case "packetUtilization":
      return row.packetUtilizationValue ?? Number.NEGATIVE_INFINITY;
    case "streamId":
      return row.streamId.toLowerCase();
    default:
      return "";
  }
}

function matchesFilter(value: string, filter: string): boolean {
  if (!filter.trim()) return true;
  return value.toLowerCase().includes(filter.trim().toLowerCase());
}

const FilterDropdown: React.FC<{
  colKey: CallQualityDetailsSortKey;
  uniqueValues: string[];
  selected: string[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onValueToggle: (value: string) => void;
  onClearColumn: () => void;
  onSelectAllColumn: (values: string[]) => void;
}> = ({
  colKey,
  uniqueValues,
  selected,
  searchValue,
  onSearchChange,
  onValueToggle,
  onClearColumn,
  onSelectAllColumn,
}) => {
    const filteredValues = useMemo(() => {
      if (!searchValue.trim()) return uniqueValues;
      const lower = searchValue.toLowerCase();
      return uniqueValues.filter((value) => value.toLowerCase().includes(lower));
    }, [searchValue, uniqueValues]);

    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild onClick={(event) => event.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 min-w-[24px] min-h-[24px] flex-shrink-0"
            aria-label="Filter column"
          >
            {selected.length > 0 ? (
              <FilterCheckIcon className="h-3 w-3" />
            ) : (
              <FilterIcon className="h-3 w-3" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-[240px] max-h-[80vh] overflow-y-auto"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="p-2">
            <p className="text-xs font-medium mb-2">Filter by values:</p>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search values..."
                className="flex-1 text-sm bg-gray-50 dark:bg-[#3C5C73] border-gray-200 dark:border-white text-primary dark:text-white placeholder-gray-500"
                value={searchValue}
                onChange={(event) => onSearchChange(event.target.value)}
              />
              {searchValue ? (
                <Button variant="ghost" size="sm" onClick={() => onSearchChange("")}>
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
            <div className="max-h-[150px] overflow-y-auto pr-2 space-y-1 mt-2 overscroll-contain" onWheel={(e) => e.stopPropagation()}>
              {filteredValues.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-2">No results found</div>
              ) : (
                filteredValues.map((value) => (
                  <div key={`${colKey}-${value}`} className="flex items-center gap-2 py-1">
                    <input
                      type="checkbox"
                      id={`${colKey}-${value}`}
                      checked={selected.includes(value)}
                      onChange={() => onValueToggle(value)}
                      className="h-4 w-4 rounded border-gray-300 flex-shrink-0"
                    />
                    <label
                      htmlFor={`${colKey}-${value}`}
                      className="text-xs truncate max-w-[190px] cursor-pointer leading-4 text-primary dark:text-white"
                      title={value}
                    >
                      {value}
                    </label>
                  </div>
                ))
              )}
            </div>
            <div className="flex justify-between mt-4 pt-2 border-t border-gray-200 dark:border-[#4A6B85]">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1 text-xs hover:bg-success hover:border-success hover:text-black dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-success dark:hover:border-success dark:hover:text-black"
                onClick={onClearColumn}
              >
                Clear
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1 text-xs hover:bg-success hover:border-success hover:text-black dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-success dark:hover:border-success dark:hover:text-black"
                onClick={() => onSelectAllColumn(uniqueValues)}
              >
                Select All
              </Button>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

function formatStreamMetric(key: string, value: unknown): React.ReactNode {
  if (value == null || value === "") return DISPLAY_NOT_AVAILABLE;
  if (typeof value !== "number") {
    if (key === "quality") {
      const qualityLabel = normalizeQualityLabel(String(value));
      return <span className={cn("font-medium", getQualityClassName(qualityLabel))}>{qualityLabel}</span>;
    }
    return String(value);
  }

  if (key === "averageJitter") {
    const text = `${formatNumber(value)} ms`;
    if (value > 30) {
      return <span className="font-bold text-red-700 dark:text-red-300">{text}</span>;
    }
    return text;
  }

  if (key === "averagePacketLossRate") {
    const packetLossPercent = value <= 1 ? value * 100 : value;
    const text = `${formatNumber(packetLossPercent)}%`;
    if (packetLossPercent > 10) {
      return <span className="font-bold text-red-700 dark:text-red-300">{text}</span>;
    }
    return text;
  }

  if (key === "averageRoundTripTime") {
    const text = `${formatNumber(value)} ms`;
    if (value > 500) {
      return <span className="font-bold text-red-700 dark:text-red-300">{text}</span>;
    }
    return text;
  }

  if (key === "averageAudioDegradation") {
    const status = value > 0.01 ? "unstable" : "stable";
    return (
      <span>
        {formatNumber(value, 4)}{" "}
        <span className={status === "unstable" ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}>
          {status}
        </span>
      </span>
    );
  }
  if (STREAM_MS_FIELDS.has(key)) return `${formatNumber(value)} ms`;
  if (STREAM_PERCENT_FIELDS.has(key)) return `${formatNumber(value * 100)}%`;
  if (key === "packetUtilization") {
    const text = `${formatNumber(value)} packets`;
    if (value < 500) {
      return <span className="font-bold text-red-700 dark:text-red-300">{text}</span>;
    }
    return text;
  }
  if (key === "averageBandwidthEstimate") return `${formatNumber(value / 1000)} kbps`;
  if (key === "averageReceivedFrameRate" || key === "averageVideoFrameRate") return `${formatNumber(value)} fps`;
  return formatNumber(value);
}

function getSegmentQuality(segment: SegmentStreamRecord): CallQualityStatus {
  const qualities = (segment.streams ?? []).map((s) => String(s.quality ?? "").toLowerCase());
  if (qualities.includes("poor") || qualities.includes("bad")) return "Poor";
  if (qualities.includes("good")) return "Good";
  return "Unclassified";
}

function getSegmentStreamRequestKeyForRow(row: CallQualityDetailRow): number | string | null {
  if (row.callUniqueId != null && Number.isFinite(row.callUniqueId)) return row.callUniqueId;
  if (row.callId) return row.callId;
  return null;
}

const CallQualityDetailsModal: React.FC<CallQualityDetailsModalProps> = ({
  open,
  onOpenChange,
  row,
}) => {
  const { theme } = useDashboard();
  const [segments, setSegments] = useState<SegmentStreamRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<CallQualityDetailsSortKey>("startTime");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [filters, setFilters] = useState<Record<CallQualityDetailsSortKey, string[]>>({
    startTime: [],
    stream: [],
    quality: [],
    duration: [],
    avgJitter: [],
    avgPackageLost: [],
    avgRoundTrip: [],
    packetUtilization: [],
    streamId: [],
  });
  const [searchValues, setSearchValues] = useState<Record<CallQualityDetailsSortKey, string>>({
    startTime: "",
    stream: "",
    quality: "",
    duration: "",
    avgJitter: "",
    avgPackageLost: "",
    avgRoundTrip: "",
    packetUtilization: "",
    streamId: "",
  });

  const segmentStreamKey = row ? getSegmentStreamRequestKeyForRow(row) : null;

  useEffect(() => {
    if (!open || segmentStreamKey == null || segmentStreamKey === "") return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setSegments([]);

    fetchSegmentStreamByCallUniqueId(segmentStreamKey, { signal: controller.signal })
      .then((data) => setSegments(data ?? []))
      .catch((err) => {
        if (err?.name !== "AbortError" && err?.name !== "CanceledError") {
          setError("Unable to load segment details.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, segmentStreamKey]);

  // Flatten segments and streams into table rows
  const tableRows: CallQualityDetailsTableRow[] = [];

  segments.forEach((segment, segmentIndex) => {
    const durationStr = typeof segment.duration === "number" ? formatSecondsToHMS(segment.duration) : DISPLAY_NOT_AVAILABLE;
    const durationText = formatDurationValue(segment.duration);
    const failureReasonLabel = getFailureReasonLabel(segment.failureType);
    const startTimeText = segment.startTime ? convertLocalTimezone(segment.startTime) : DISPLAY_NOT_AVAILABLE;
    const startTimeValue = segment.startTime ? new Date(segment.startTime).getTime() : null;

    if (!segment.streams || segment.streams.length === 0) {
      // Add a row for segment without streams
      const segmentQuality = getSegmentQuality(segment);
      const streamText = failureReasonLabel ? `${DISPLAY_NOT_AVAILABLE} (${failureReasonLabel})` : INTERNAL_NOT_AVAILABLE;
      tableRows.push({
        id: `seg-${segmentIndex}-no-stream`,
        startTimeText,
        startTimeValue,
        streamId: DISPLAY_NOT_AVAILABLE,
        streamDirection: (
          <span>
            {DISPLAY_NOT_AVAILABLE}
            {failureReasonLabel ? (
              <span className="font-bold text-red-700 dark:text-red-300"> ({failureReasonLabel})</span>
            ) : null}
          </span>
        ),
        streamText,
        quality: segmentQuality,
        qualityStyle: getQualityBadgeStyle(segmentQuality, theme ?? "light"),
        duration: durationStr,
        durationText,
        durationValue: segment.duration ?? null,
        avgJitter: DISPLAY_NOT_AVAILABLE,
        avgJitterText: INTERNAL_NOT_AVAILABLE,
        avgJitterValue: null,
        avgPackageLost: DISPLAY_NOT_AVAILABLE,
        avgPackageLostText: INTERNAL_NOT_AVAILABLE,
        avgPackageLostValue: null,
        avgRoundTrip: DISPLAY_NOT_AVAILABLE,
        avgRoundTripText: INTERNAL_NOT_AVAILABLE,
        avgRoundTripValue: null,
        packetUtilization: DISPLAY_NOT_AVAILABLE,
        packetUtilizationText: INTERNAL_NOT_AVAILABLE,
        packetUtilizationValue: null,
      });
    } else {
      segment.streams.forEach((stream, streamIndex) => {
        const streamQuality = normalizeQualityLabel(stream.quality);
        const directionText = getDirectionLabel(
          stream.streamDirection,
          segment.caller ?? "Caller",
          segment.callee ?? "Callee"
        );
        const streamText = failureReasonLabel ? `${directionText} (${failureReasonLabel})` : directionText;
        const avgJitterValue = typeof stream.averageJitter === "number" ? stream.averageJitter : null;
        const avgPacketLossRaw = typeof stream.averagePacketLossRate === "number" ? stream.averagePacketLossRate : null;
        const avgPacketLossValue = avgPacketLossRaw == null ? null : avgPacketLossRaw <= 1 ? avgPacketLossRaw * 100 : avgPacketLossRaw;
        const avgRoundTripValue = typeof stream.averageRoundTripTime === "number" ? stream.averageRoundTripTime : null;
        const packetUtilizationRaw = typeof stream.packetUtilization === "number" ? stream.packetUtilization : null;
        tableRows.push({
          id: `seg-${segmentIndex}-stream-${streamIndex}-${stream.streamId ?? ""}`,
          startTimeText,
          startTimeValue,
          streamId: stream.streamId ?? String(streamIndex + 1),
          streamDirection: (
            <span>
              {directionText}
              {failureReasonLabel ? (
                <span className="font-bold text-red-700 dark:text-red-300"> ({failureReasonLabel})</span>
              ) : null}
            </span>
          ),
          streamText,
          quality: streamQuality,
          qualityStyle: getQualityBadgeStyle(streamQuality as CallQualityStatus, theme ?? "light"),
          duration: durationStr,
          durationText,
          durationValue: segment.duration ?? null,
          avgJitter: formatStreamMetric("averageJitter", stream.averageJitter),
          avgJitterText: formatMetricText(avgJitterValue, "ms"),
          avgJitterValue,
          avgPackageLost: formatStreamMetric("averagePacketLossRate", stream.averagePacketLossRate),
          avgPackageLostText: formatPacketLossValue(avgPacketLossRaw),
          avgPackageLostValue: avgPacketLossValue,
          avgRoundTrip: formatStreamMetric("averageRoundTripTime", stream.averageRoundTripTime),
          avgRoundTripText: formatMetricText(avgRoundTripValue, "ms"),
          avgRoundTripValue,
          packetUtilization: formatStreamMetric("packetUtilization", stream.packetUtilization as number | undefined),
          packetUtilizationText: packetUtilizationRaw != null ? `${formatNumber(packetUtilizationRaw)} packets` : INTERNAL_NOT_AVAILABLE,
          packetUtilizationValue: packetUtilizationRaw,
        });
      });
    }
  });

  const filteredRows = useMemo(() => {
    return tableRows.filter((tableRow) => {
      return (
        (filters.startTime.length === 0 || filters.startTime.includes(tableRow.startTimeText)) &&
        (filters.stream.length === 0 || filters.stream.includes(tableRow.streamText)) &&
        (filters.quality.length === 0 || filters.quality.includes(tableRow.quality)) &&
        (filters.duration.length === 0 || filters.duration.includes(tableRow.durationText)) &&
        (filters.avgJitter.length === 0 || filters.avgJitter.includes(tableRow.avgJitterText)) &&
        (filters.avgPackageLost.length === 0 || filters.avgPackageLost.includes(tableRow.avgPackageLostText)) &&
        (filters.avgRoundTrip.length === 0 || filters.avgRoundTrip.includes(tableRow.avgRoundTripText)) &&
        (filters.packetUtilization.length === 0 || filters.packetUtilization.includes(tableRow.packetUtilizationText)) &&
        (filters.streamId.length === 0 || filters.streamId.includes(tableRow.streamId))
      );
    });
  }, [filters, tableRows]);

  const columnUniqueValues = useMemo<Record<CallQualityDetailsSortKey, string[]>>(() => {
    const unique = {
      startTime: new Set<string>(),
      stream: new Set<string>(),
      quality: new Set<string>(),
      duration: new Set<string>(),
      avgJitter: new Set<string>(),
      avgPackageLost: new Set<string>(),
      avgRoundTrip: new Set<string>(),
      packetUtilization: new Set<string>(),
      streamId: new Set<string>(),
    };

    tableRows.forEach((tableRow) => {
      if (tableRow.startTimeText && tableRow.startTimeText !== DISPLAY_NOT_AVAILABLE) unique.startTime.add(tableRow.startTimeText);
      if (tableRow.streamText && tableRow.streamText !== INTERNAL_NOT_AVAILABLE) unique.stream.add(tableRow.streamText);
      if (tableRow.quality && tableRow.quality !== INTERNAL_NOT_AVAILABLE) unique.quality.add(tableRow.quality);
      if (tableRow.durationText && tableRow.durationText !== INTERNAL_NOT_AVAILABLE) unique.duration.add(tableRow.durationText);
      if (tableRow.avgJitterText && tableRow.avgJitterText !== INTERNAL_NOT_AVAILABLE) unique.avgJitter.add(tableRow.avgJitterText);
      if (tableRow.avgPackageLostText && tableRow.avgPackageLostText !== INTERNAL_NOT_AVAILABLE) unique.avgPackageLost.add(tableRow.avgPackageLostText);
      if (tableRow.avgRoundTripText && tableRow.avgRoundTripText !== INTERNAL_NOT_AVAILABLE) unique.avgRoundTrip.add(tableRow.avgRoundTripText);
      if (tableRow.packetUtilizationText && tableRow.packetUtilizationText !== INTERNAL_NOT_AVAILABLE) unique.packetUtilization.add(tableRow.packetUtilizationText);
      if (tableRow.streamId && tableRow.streamId !== DISPLAY_NOT_AVAILABLE) unique.streamId.add(tableRow.streamId);
    });

    return {
      startTime: Array.from(unique.startTime),
      stream: Array.from(unique.stream),
      quality: Array.from(unique.quality),
      duration: Array.from(unique.duration),
      avgJitter: Array.from(unique.avgJitter),
      avgPackageLost: Array.from(unique.avgPackageLost),
      avgRoundTrip: Array.from(unique.avgRoundTrip),
      packetUtilization: Array.from(unique.packetUtilization),
      streamId: Array.from(unique.streamId),
    };
  }, [tableRows]);

  const sortedRows = useMemo(() => {
    const rowsCopy = [...filteredRows];
    rowsCopy.sort((left, right) => {
      const leftValue = getSortValue(left, sortKey);
      const rightValue = getSortValue(right, sortKey);
      if (typeof leftValue === "number" && typeof rightValue === "number") {
        return sortDirection === "asc" ? leftValue - rightValue : rightValue - leftValue;
      }

      const result = String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: "base" });
      return sortDirection === "asc" ? result : -result;
    });
    return rowsCopy;
  }, [filteredRows, sortDirection, sortKey]);

  const hasActiveFilters = Object.values(filters).some((value) => value.length > 0);

  const handleSort = (key: CallQualityDetailsSortKey) => {
    if (sortKey === key) {
      setSortDirection((previous) => (previous === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const handleValueToggle = (key: CallQualityDetailsSortKey, value: string) => {
    setFilters((previous) => {
      const currentValues = previous[key];
      return {
        ...previous,
        [key]: currentValues.includes(value)
          ? currentValues.filter((currentValue) => currentValue !== value)
          : [...currentValues, value],
      };
    });
  };

  const handleClearColumn = (key: CallQualityDetailsSortKey) => {
    setFilters((previous) => ({ ...previous, [key]: [] }));
  };

  const handleSelectAllColumn = (key: CallQualityDetailsSortKey, values: string[]) => {
    setFilters((previous) => ({ ...previous, [key]: values }));
  };

  const clearFilters = () => {
    setFilters({
      startTime: [],
      stream: [],
      quality: [],
      duration: [],
      avgJitter: [],
      avgPackageLost: [],
      avgRoundTrip: [],
      packetUtilization: [],
      streamId: [],
    });
    setSearchValues({
      startTime: "",
      stream: "",
      quality: "",
      duration: "",
      avgJitter: "",
      avgPackageLost: "",
      avgRoundTrip: "",
      packetUtilization: "",
      streamId: "",
    });
  };

  const renderHeaderCell = (key: CallQualityDetailsSortKey, label: string, align: "left" | "right" = "left", tooltip?: string) => (
    <div className={cn("flex items-center gap-1", align === "right" && "justify-end")}>
      {tooltip && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 shrink-0 cursor-pointer opacity-60 hover:opacity-100" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[220px] text-xs text-left">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <button
        type="button"
        className={cn(
          "flex items-center gap-1 text-left font-semibold",
          align === "right" && "justify-end"
        )}
        onClick={() => handleSort(key)}
      >
        <span>{label}</span>
        {sortKey === key ? (
          sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
        ) : null}
      </button>
      <FilterDropdown
        colKey={key}
        uniqueValues={columnUniqueValues[key]}
        selected={filters[key]}
        searchValue={searchValues[key]}
        onSearchChange={(value) => setSearchValues((previous) => ({ ...previous, [key]: value }))}
        onValueToggle={(value) => handleValueToggle(key, value)}
        onClearColumn={() => handleClearColumn(key)}
        onSelectAllColumn={(values) => handleSelectAllColumn(key, values)}
      />
    </div>
  );

  if (!row) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            "w-[98vw] max-w-[98vw] max-h-[90vh] rounded-xl p-0 gap-0 overflow-hidden flex flex-col",
            theme === "dark" ? "bg-[#0C3451] text-white border-[#3C5C73]" : "bg-white"
          )}
          closeClassName={theme === "dark" ? "text-white hover:opacity-80" : ""}
        >
          <DialogHeader className="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-[#3C5C73]">
            <DialogTitle className={cn("text-sm font-semibold pr-8", theme === "dark" ? "text-white" : "text-primary")}>
              Call Quality Details
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 text-sm">
            {error && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-950/20 dark:border-red-800 dark:text-red-300">
                {error}
              </div>
            )}

            <div className="mb-3 flex items-center justify-between text-sm text-muted-foreground dark:text-white/70">
              <span>Start Time: {convertLocalTimezone(row.startTime)}</span>
              {hasActiveFilters ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  onClick={clearFilters}
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear Filters
                </Button>
              ) : null}
            </div>

            {loading ? (
              <div className="mt-4 text-sm text-muted-foreground dark:text-white/70">Loading segment stream data...</div>
            ) : (
              <div className="rounded-md border border-gray-200 dark:border-[#3C5C73] overflow-x-auto">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow className="bg-muted/50 dark:bg-[#1A3F5C] dark:hover:bg-[#1A3F5C] dark:border-[#3C5C73]">
                      <TableHead className="font-semibold text-primary dark:text-white whitespace-nowrap min-w-[130px]">{renderHeaderCell("startTime", "Start Time")}</TableHead>
                      <TableHead className="font-semibold text-primary dark:text-white whitespace-nowrap min-w-[240px]">{renderHeaderCell("stream", "Stream")}</TableHead>
                      <TableHead className="font-semibold text-primary dark:text-white min-w-[110px]">{renderHeaderCell("quality", "Quality")}</TableHead>
                      <TableHead className="font-semibold text-primary dark:text-white min-w-[100px]">{renderHeaderCell("duration", "Duration")}</TableHead>
                      <TableHead className="font-semibold text-primary dark:text-white text-right min-w-[110px]">{renderHeaderCell("avgJitter", "Avg Jitter", "right", "Average variation in packet arrival times, which can cause choppy audio. Poor if greater than 30ms")}</TableHead>
                      <TableHead className="font-semibold text-primary dark:text-white text-right min-w-[130px]">{renderHeaderCell("avgPackageLost", "Avg Package Lost", "right", "Percentage of data packets that never arrive, causing audio gaps or distortion. Poor if greater than 10% (0.1)")}</TableHead>
                      <TableHead className="font-semibold text-primary dark:text-white text-right min-w-[130px]">{renderHeaderCell("avgRoundTrip", "Avg Round Trip", "right", "Time (ms) for a packet to travel to the receiver and back, measuring network latency. Poor if greater than 500ms")}</TableHead>
                      <TableHead className="font-semibold text-primary dark:text-white text-right min-w-[130px]">{renderHeaderCell("packetUtilization", "Package Utilization", "right", "The stream will be classified once its packet utilization exceeds 500")}</TableHead>
                      <TableHead className="font-semibold text-primary dark:text-white whitespace-nowrap min-w-[110px]">{renderHeaderCell("streamId", "Stream ID")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedRows.length === 0 ? (
                      <TableRow className="dark:border-[#3C5C73] dark:hover:bg-[#1A3F5C]/50">
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          {tableRows.length === 0 ? "No data available" : "No records match the current filters"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedRows.map((tr) => {
                        return (
                          <TableRow key={tr.id} className="dark:border-[#3C5C73] dark:hover:bg-[#1A3F5C]/50">
                            <TableCell>{tr.startTimeText}</TableCell>
                            <TableCell>{tr.streamDirection}</TableCell>
                            <TableCell>
                              <span
                                className="inline-flex px-2 py-0.5 rounded text-xs font-medium"
                                style={tr.qualityStyle}
                              >
                                {tr.quality}
                              </span>
                            </TableCell>
                            <TableCell>{tr.duration}</TableCell>
                            <TableCell className="text-right">{tr.avgJitter}</TableCell>
                            <TableCell className="text-right">{tr.avgPackageLost}</TableCell>
                            <TableCell className="text-right">{tr.avgRoundTrip}</TableCell>
                            <TableCell className="text-right">{tr.packetUtilization}</TableCell>
                            <TableCell>{tr.streamId}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CallQualityDetailsModal;
